// @givmo/fundraisers-module — fundraiser designations editor (CPB-WEB1).
//
// A fundraiser's designations are the donor-facing "designate my gift" options (server-resolved into
// `donation.instructions`, the §321(a)(8) plumbing — D-CAMP-5). This editor submits the FULL desired
// list to `PUT …/fundraisers/{id}/designations`; the server reconciles it (match-by-id / insert /
// soft-retire-if-referenced / hard-delete-if-not — invariant 6). So a label that already has donations is
// never destroyed: dropping it here soft-retires it, and it comes back on the next read as an inactive row
// we render muted ("retired — has donations") and DO NOT resubmit (omitting it keeps it retired).
//
// The list is capped at ≤25 ACTIVE rows (the submitted list); reordering is up/down via <RowActions>
// (no drag and drop); duplicate labels are blocked client-side (the DB unique constraint is the backstop).

import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { sdk, isGivmoError, type FundraiserDesignationOut } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  RowActions,
  TextField,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ArchiveX, ListPlus, Plus } from 'lucide-react';
import { useLivePage } from './use-live-page';

const PREFIX = 'designation';
const MAX_DESIGNATIONS = 25;
const LABEL_MAX = 120;

/** One editable active row. `id` present = an existing designation (reconciled by id); absent = new. */
interface EditRow {
  /** Stable local key (React list identity; NOT the server id). */
  key: string;
  id?: string;
  label: string;
}

export interface DesignationsEditorProps {
  orgRef: string;
  fundraiserId: string;
  /** The fundraiser's designations (active + retired) from the loaded `OrgFundraiserOut`. */
  designations: FundraiserDesignationOut[];
  /** Invalidate/refresh the fundraiser query after a successful save. */
  onSaved: () => void | Promise<unknown>;
  /** Read-only (a CLOSED fundraiser) — render the list without edit controls. */
  disabled?: boolean;
}

let keyCounter = 0;
function nextKey(): string {
  keyCounter += 1;
  return `d-${keyCounter}`;
}

/** Seed the editable rows from the loaded active designations, ordered by server position. */
function seedRows(designations: FundraiserDesignationOut[]): EditRow[] {
  return designations
    .filter((d) => d.is_active)
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((d) => ({ key: nextKey(), id: d.id, label: d.label }));
}

/** A dirty/identity signature of the active rows (id + trimmed label + order). */
function signature(rows: EditRow[]): string {
  return rows.map((r) => `${r.id ?? ''}:${r.label.trim()}`).join('|');
}

/**
 * The same signature computed from a submitted PUT payload — used to advance the dirty baseline on a
 * CONFIRMED save. Matches `signature(rows)` for the saved rows: payload labels are already trimmed and
 * the order matches, so `${id}:${label}` lines are identical.
 */
function payloadSignature(payload: { id?: string; label: string; position: number }[]): string {
  return payload.map((p) => `${p.id ?? ''}:${p.label}`).join('|');
}

export function DesignationsEditor({
  orgRef,
  fundraiserId,
  designations,
  onSaved,
  disabled = false,
}: DesignationsEditorProps) {
  const live = useLivePage();
  const retired = useMemo(() => designations.filter((d) => !d.is_active), [designations]);

  // Seed once per set of loaded designations; the seed signature is the dirty baseline.
  const seeded = useMemo(() => seedRows(designations), [designations]);
  const [rows, setRows] = useState<EditRow[]>(seeded);
  const [baseline, setBaseline] = useState<string>(() => signature(seeded));
  const [seedSig, setSeedSig] = useState<string>(() => signature(seeded));

  // Re-seed if the loaded designations changed identity underneath us (e.g. after a save elsewhere).
  const currentSeedSig = signature(seeded);
  if (currentSeedSig !== seedSig && signature(rows) === baseline) {
    setRows(seeded);
    setBaseline(currentSeedSig);
    setSeedSig(currentSeedSig);
  }

  const save = useMutation({
    mutationFn: (payload: { id?: string; label: string; position: number }[]) =>
      sdk.orgFundraisersPutOrgFundraiserDesignations({
        path: { org_ref: orgRef, fundraiser_id: fundraiserId },
        body: { designations: payload },
        throwOnError: true,
      }),
    // Advance the dirty baseline ONLY on a confirmed save (from the submitted payload — the mutation
    // `variables`). Never advance it optimistically: an errored PUT (e.g. a re-added label colliding
    // with a retired label's unique constraint — the client dup-check ignores retired rows) must leave
    // the form dirty so "Save designations" stays enabled and the user can retry.
    onSuccess: async (_data, payload) => {
      if (live()) setBaseline(payloadSignature(payload));
      await onSaved();
      if (live()) toast.success('Designations saved');
    },
    onError: (error) => {
      if (!live()) return;
      toast.error(
        isGivmoError(error)
          ? error.message
          : 'We couldn’t save your designations. Please try again.',
      );
    },
  });

  const trimmedLabels = rows.map((r) => r.label.trim());
  const duplicateLabels = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const label of trimmedLabels) {
      if (label.length === 0) continue;
      if (seen.has(label)) dupes.add(label);
      seen.add(label);
    }
    return dupes;
  }, [trimmedLabels]);

  const hasEmpty = trimmedLabels.some((l) => l.length === 0);
  const hasTooLong = trimmedLabels.some((l) => l.length > LABEL_MAX);
  const isValid = !hasEmpty && !hasTooLong && duplicateLabels.size === 0;
  const isDirty = signature(rows) !== baseline;
  const atCap = rows.length >= MAX_DESIGNATIONS;

  const updateLabel = (key: string, label: string) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, label } : r)));

  const addRow = () =>
    setRows((prev) =>
      prev.length >= MAX_DESIGNATIONS ? prev : [...prev, { key: nextKey(), label: '' }],
    );

  const removeRow = (key: string) => setRows((prev) => prev.filter((r) => r.key !== key));

  const move = (index: number, delta: number) =>
    setRows((prev) => {
      const next = prev.slice();
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved!);
      return next;
    });

  const onSubmit = () => {
    if (disabled || !isValid || !isDirty || save.isPending) return;
    const payload = rows.map((r, i) => ({
      ...(r.id ? { id: r.id } : {}),
      label: r.label.trim(),
      position: i,
    }));
    // The baseline advances in `onSuccess` (not here) so a failed save leaves the form dirty/retryable.
    save.mutate(payload);
  };

  return (
    <Card testId={`${PREFIX}s-card`}>
      <CardHeader
        title="Designations"
        subtitle="Optional funds a supporter can direct their gift to. Leave empty to keep it simple."
      />
      <CardBody className="flex flex-col gap-12">
        {rows.length === 0 ? (
          <p className="text-s13 text-text-tertiary" {...testIdProp(`${PREFIX}s-empty`)}>
            No designations yet. Supporters give to the fundraiser directly.
          </p>
        ) : (
          <ul className="flex flex-col gap-8" {...testIdProp(`${PREFIX}s-list`)}>
            {rows.map((row, index) => {
              const trimmed = row.label.trim();
              const isDup = trimmed.length > 0 && duplicateLabels.has(trimmed);
              const isLong = trimmed.length > LABEL_MAX;
              const error = isDup
                ? 'Duplicate designation'
                : isLong
                  ? `Keep it under ${LABEL_MAX} characters`
                  : undefined;
              return (
                <li
                  key={row.key}
                  className="flex items-start gap-8"
                  {...testIdProp(`${PREFIX}-row`)}
                >
                  <div className="min-w-0 flex-1">
                    <TextField
                      aria-label={`Designation ${index + 1} label`}
                      placeholder="e.g. General fund, Scholarships, Building"
                      value={row.label}
                      maxLength={LABEL_MAX + 1}
                      disabled={disabled}
                      error={!!error}
                      errorMessage={error}
                      onChange={(e) => updateLabel(row.key, e.target.value)}
                      testId={`${PREFIX}-label-input-${index}`}
                    />
                  </div>
                  {!disabled && (
                    <div className="pt-4">
                      <RowActions
                        contentClassName="fundraisers-menu"
                        label={`Actions for designation ${index + 1}`}
                        testId={`${PREFIX}-actions-${index}`}
                        actions={[
                          {
                            label: 'Move up',
                            onSelect: () => move(index, -1),
                            disabled: index === 0,
                            testId: `${PREFIX}-up-${index}`,
                          },
                          {
                            label: 'Move down',
                            onSelect: () => move(index, 1),
                            disabled: index === rows.length - 1,
                            testId: `${PREFIX}-down-${index}`,
                          },
                          {
                            label: 'Remove',
                            onSelect: () => removeRow(row.key),
                            destructive: true,
                            testId: `${PREFIX}-remove-${index}`,
                          },
                        ]}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* Retired designations — soft-retired because they have donations; history, not editable. */}
        {retired.length > 0 && (
          <ul className="flex flex-col gap-6" {...testIdProp(`${PREFIX}s-retired`)}>
            {retired.map((d) => (
              <li
                key={d.id}
                className="flex items-center gap-8 rounded-md border border-border-subtle bg-surface-sunken px-10 py-8"
                {...testIdProp(`${PREFIX}-retired-row`)}
              >
                <Icon
                  icon={ArchiveX}
                  size={14}
                  className="shrink-0 text-text-tertiary"
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-s13 text-text-tertiary line-through">
                  {d.label}
                </span>
                <span className="shrink-0 text-s12 text-text-tertiary">
                  retired — has donations
                </span>
              </li>
            ))}
          </ul>
        )}

        {!disabled && (
          <div className="flex items-center justify-between gap-8">
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
              disabled={atCap}
              onClick={addRow}
              testId={`${PREFIX}-add`}
            >
              Add designation
            </Button>
            <div className="flex items-center gap-10">
              {atCap && (
                <span className="text-s12 text-text-tertiary">Up to {MAX_DESIGNATIONS}</span>
              )}
              <Button
                appearance="saas"
                tone="primary"
                size="sm"
                leadingIcon={<Icon icon={ListPlus} size={14} aria-hidden />}
                disabled={!isValid || !isDirty}
                loading={save.isPending}
                onClick={onSubmit}
                testId={`${PREFIX}s-save`}
              >
                Save designations
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
